import React, { useCallback, useEffect, useState } from 'react';
import submissionWorkflowService, {
  SubmissionDetail, SubmissionStatus, RejectionReason,
  SUBMISSION_STATUS_CONFIG, REJECTION_LABEL,
} from '../../../services/submissionWorkflow.service';
import InterviewListe from './InterviewListe';
import { relativeZeit } from '../workflow/zeit';

/**
 * Ein Kundenprozess mit allem, was dazugehört: Status, Gespräche, Vermittlung,
 * Verlauf (Regel 29).
 */

interface Props {
  submissionId: string;
  onGeaendert?: () => void;
  onSchliessen: () => void;
}

const ABSAGEN: SubmissionStatus[] = ['REJECTED_CLIENT', 'REJECTED_CANDIDATE'];

const feld: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: '0.82rem',
  borderRadius: 'var(--radius-md)', border: '1px solid var(--glass-border)',
  background: 'var(--glass)', color: 'var(--text-primary)',
};

export const ProzessDetail: React.FC<Props> = ({ submissionId, onGeaendert, onSchliessen }) => {
  const [daten, setDaten] = useState<SubmissionDetail | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [absageFuer, setAbsageFuer] = useState<SubmissionStatus | null>(null);
  const [grund, setGrund] = useState<RejectionReason>('CLIENT_DECISION');
  const [notiz, setNotiz] = useState('');
  const [vermittlungOffen, setVermittlungOffen] = useState(false);
  const [eintritt, setEintritt] = useState('');
  const [gehalt, setGehalt] = useState('');
  const [satz, setSatz] = useState('');

  const laden = useCallback(async () => {
    try {
      setLaedt(true);
      const d = await submissionWorkflowService.detail(submissionId);
      setDaten(d);
      setSatz(d.submission.job_posting?.fee_percent ? String(d.submission.job_posting.fee_percent) : '');
      setFehler(null);
    } catch (e: any) {
      setFehler(e?.message || 'Der Prozess konnte nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [submissionId]);

  useEffect(() => { laden(); }, [laden]);

  const statusSetzen = async (status: SubmissionStatus) => {
    setFehler(null);
    if (ABSAGEN.includes(status) && absageFuer !== status) {
      setAbsageFuer(status);
      return;
    }
    try {
      setLaeuft(true);
      await submissionWorkflowService.setzeStatus(submissionId, status,
        ABSAGEN.includes(status) ? { rejection_reason: grund, note: notiz.trim() || undefined } : {});
      setAbsageFuer(null);
      setNotiz('');
      await laden();
      onGeaendert?.();
    } catch (e: any) {
      setFehler(e?.message || 'Der Status konnte nicht geändert werden.');
    } finally {
      setLaeuft(false);
    }
  };

  const vermitteln = async () => {
    setFehler(null);
    if (!eintritt) { setFehler('Das Eintrittsdatum wird benötigt.'); return; }
    try {
      setLaeuft(true);
      await submissionWorkflowService.vermittlungAnlegen(submissionId, {
        start_date: eintritt,
        annual_salary: gehalt ? Number(gehalt) : null,
        fee_percent: satz ? Number(satz) : null,
      });
      setVermittlungOffen(false);
      await laden();
      onGeaendert?.();
    } catch (e: any) {
      setFehler(e?.message || 'Die Vermittlung konnte nicht angelegt werden.');
    } finally {
      setLaeuft(false);
    }
  };

  if (laedt && !daten) {
    return <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', padding: '10px' }}>Prozess wird geladen…</div>;
  }
  if (!daten) {
    return (
      <div style={{ padding: '10px' }}>
        <div style={{ color: '#EF4444', fontSize: '0.85rem' }}>{fehler || 'Nicht gefunden.'}</div>
        <button className="btn btn-ghost" style={{ fontSize: '0.78rem', marginTop: '8px' }} onClick={onSchliessen}>Zurück</button>
      </div>
    );
  }

  const s = daten.submission;
  const konfig = SUBMISSION_STATUS_CONFIG[s.process_status];
  const ziele = (s.allowedTargets || []).filter((z) => z !== 'PLACED');
  const kannVermitteln = s.process_status === 'OFFER_ACCEPTED' && !daten.placement;

  return (
    <div style={{ padding: '4px 0' }}>
      <button className="btn btn-ghost" style={{ fontSize: '0.78rem', marginBottom: '10px' }} onClick={onSchliessen}>
        ← Alle Kundenprozesse
      </button>

      <div style={{
        padding: '12px', borderRadius: 'var(--radius-md)',
        border: '1px solid var(--glass-border)', background: 'var(--glass)', marginBottom: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {s.client_company?.name || 'Kunde offen'}
          </span>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            {s.job_posting?.title || 'Ohne Stellenauftrag'}
          </span>
          <span style={{
            marginLeft: 'auto', fontSize: '0.74rem', fontWeight: 700,
            color: konfig.color, background: konfig.bgColor,
            border: `1px solid ${konfig.color}40`, borderRadius: '999px', padding: '2px 9px',
          }}>
            {konfig.label}
          </span>
        </div>
        <div style={{ fontSize: '0.76rem', color: 'var(--text-tertiary)', marginTop: '4px' }}>
          seit {relativeZeit(s.status_changed_at || s.created_at)}
          {s.job_posting?.location ? ` · ${s.job_posting.location}` : ''}
          {s.rejection_reason ? ` · Grund: ${REJECTION_LABEL[s.rejection_reason]}` : ''}
        </div>
      </div>

      {fehler && <div style={{ color: '#EF4444', fontSize: '0.8rem', marginBottom: '8px' }}>{fehler}</div>}

      {ziele.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginBottom: '5px' }}>
            Nächster Schritt
          </div>
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {ziele.map((z) => (
              <button
                key={z}
                className="btn btn-ghost"
                style={{ fontSize: '0.76rem', padding: '3px 9px', color: SUBMISSION_STATUS_CONFIG[z].color }}
                disabled={laeuft}
                onClick={() => statusSetzen(z)}
              >
                {SUBMISSION_STATUS_CONFIG[z].label}
              </button>
            ))}
          </div>

          {absageFuer && (
            <div style={{ marginTop: '8px', display: 'grid', gap: '6px', maxWidth: '420px' }}>
              <select style={feld} value={grund} onChange={(e) => setGrund(e.target.value as RejectionReason)}>
                {(Object.keys(REJECTION_LABEL) as RejectionReason[]).map((r) => (
                  <option key={r} value={r}>{REJECTION_LABEL[r]}</option>
                ))}
              </select>
              <input style={feld} value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Anmerkung (optional)" />
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="btn btn-primary" style={{ fontSize: '0.76rem' }} disabled={laeuft}
                  onClick={() => statusSetzen(absageFuer)}>
                  {SUBMISSION_STATUS_CONFIG[absageFuer].label} eintragen
                </button>
                <button className="btn btn-ghost" style={{ fontSize: '0.76rem' }} onClick={() => setAbsageFuer(null)}>
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {kannVermitteln && (
        <div style={{ marginBottom: '12px' }}>
          {vermittlungOffen ? (
            <div style={{
              padding: '10px', display: 'grid', gap: '8px', maxWidth: '460px',
              borderRadius: 'var(--radius-md)', border: '1px solid var(--glass-border)', background: 'var(--glass)',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 90px', gap: '8px' }}>
                <div>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>Eintritt</span>
                  <input type="date" style={feld} value={eintritt} onChange={(e) => setEintritt(e.target.value)} />
                </div>
                <div>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>Jahresgehalt</span>
                  <input type="number" style={feld} value={gehalt} onChange={(e) => setGehalt(e.target.value)} placeholder="80000" />
                </div>
                <div>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>Honorar %</span>
                  <input type="number" style={feld} value={satz} onChange={(e) => setSatz(e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="btn btn-primary" style={{ fontSize: '0.78rem' }} disabled={laeuft} onClick={vermitteln}>
                  {laeuft ? 'Speichert…' : 'Vermittlung erfassen'}
                </button>
                <button className="btn btn-ghost" style={{ fontSize: '0.78rem' }} onClick={() => setVermittlungOffen(false)}>
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <button className="btn btn-primary" style={{ fontSize: '0.8rem' }} onClick={() => setVermittlungOffen(true)}>
              Vermittlung erfassen
            </button>
          )}
        </div>
      )}

      {daten.placement && (
        <div style={{
          padding: '10px', marginBottom: '12px', borderRadius: 'var(--radius-md)',
          border: '1px solid #10B98140', background: 'rgba(16,185,129,0.08)',
        }}>
          <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#10B981' }}>Vermittelt</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
            Eintritt {new Date(daten.placement.start_date).toLocaleDateString('de-DE')}
            {daten.placement.fee_amount ? ` · Honorar ${Number(daten.placement.fee_amount).toLocaleString('de-DE')} €` : ''}
          </div>
        </div>
      )}

      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>
          Gespräche
        </div>
        <InterviewListe submissionId={submissionId} interviews={daten.interviews} onGeaendert={() => { laden(); onGeaendert?.(); }} />
      </div>

      {daten.verlauf.length > 0 && (
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>
            Verlauf
          </div>
          <div style={{ display: 'grid', gap: '3px' }}>
            {daten.verlauf.map((v) => (
              <div key={v.id} style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)' }}>
                {relativeZeit(v.changed_at)} ·{' '}
                {v.from_process_status
                  ? `${SUBMISSION_STATUS_CONFIG[v.from_process_status as SubmissionStatus]?.label || v.from_process_status} → `
                  : ''}
                {SUBMISSION_STATUS_CONFIG[v.to_process_status as SubmissionStatus]?.label || v.to_process_status}
                {v.reason ? ` (${REJECTION_LABEL[v.reason as RejectionReason] || v.reason})` : ''}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProzessDetail;
